import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { Store } from "@reduxjs/toolkit";
import { RootState } from "src/store";
import { openForbiddenModal, updateSelectedStudy } from "src/pages/Studies/states/studiesSlice";
import { accessErrorTypes, roleAccessType, SupportedLanguageType, userAccessType } from "src/pages/Studies/models/interfaces";
import { setAccessType } from "src/pages/ManageStudies/states/slice/manageStudySlice";
import { setBlockedFrom429 } from "src/pages/NewAskKural/states/governanceSlice";
import { IGenai429Detail } from "src/pages/NewAskKural/models/interfaces";
import { environment } from "src/config/environment";
import { apiTokenRequest } from "../config/authConfig";
import { ensureMsalReady, msalInstance } from "./msalService";
import { teamsAuthService } from "./teamsService";
import { clearCognitoToken, ensureValidCognitoToken, forceRefreshCognitoToken } from "./cognitoAuth";
import { isCognitoLoginAllowed, isEntraEnabled } from "src/config/loginMethods";

let store: Store;
export const injectStore = (_store: Store) => {
	store = _store;
};

let isAuthenticating = false;
const isCognitoEnabled = isCognitoLoginAllowed;

/** Build the in-app /login URL, honoring REACT_APP_BASE_PATH. */
const getLoginPath = (): string => {
	const basePath = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
	return `${basePath}/login`;
};

/** Bounce to /login on 401 (Cognito flow); guarded against /login → /login loops. */
const redirectToInAppLogin = (): void => {
	if (typeof window === "undefined") return;
	const loginPath = getLoginPath();
	if (!window.location.pathname.endsWith("/login")) {
		window.location.href = loginPath;
	}
};

/**
 * Get access token - prioritize Teams SSO if available
 */
export const getAccessToken = async (): Promise<string | null> => {
	try {
		// First, check if we're in Teams and can get a Teams token
		if (teamsAuthService.isRunningInTeams()) {
			const teamsToken = await teamsAuthService.getTeamsToken();
			if (teamsToken) {
				return teamsToken;
			}
		}

		// Fall back to MSAL for non-Teams or if Teams token fails
		await ensureMsalReady();

		const accounts = msalInstance.getAllAccounts();

		if (accounts.length === 0) {
			return null;
		}

		const response = await msalInstance.acquireTokenSilent({
			...apiTokenRequest,
			account: accounts[0],
		});
		return response.accessToken;
	} catch (error) {
		console.error("Error acquiring access token:", error);
		return null;
	}
};

/**
 * Returns the active bearer token regardless of auth provider.
 * Mirrors the exact priority used by the axiosInstance interceptor:
 *   1. Cognito (Amplify)
 *   2. Teams SSO
 *   3. Azure Entra / MSAL
 * Use this in raw fetch() calls (streaming, downloads) instead of
 * calling getAccessToken() directly, which misses Cognito tenants.
 */
export const getBearerToken = async (): Promise<string | null> => {
	if (isCognitoEnabled) {
		const cognitoToken = await ensureValidCognitoToken();
		if (cognitoToken) return cognitoToken;
	}
	return getAccessToken();
};

const axiosInstance = axios.create({
	baseURL: environment.REACT_APP_API_URL,
});


axiosInstance.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		// IdP selection is a server-side, per-tenant decision (gateway IDP_PROVIDER env var).
		// The client no longer signals it; it only attaches the right credential.
		// Amplify auto-refreshes within its own buffer when the token is near expiry.
		const cognitoAccessToken = isCognitoEnabled ? await ensureValidCognitoToken() : null;
		if (cognitoAccessToken) {
			config.withCredentials = false;
			config.headers.Authorization = `Bearer ${cognitoAccessToken}`;
		} else {
			config.withCredentials = true;

			// Add token (Teams SSO or Azure Entra ID) for Entra builds; IDM-only builds
			// rely on the cookie carried by withCredentials and skip token acquisition.
			const token = await getAccessToken();
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
				config.headers["X-Auth-Source"] = teamsAuthService.isRunningInTeams() ? "teams-sso" : "azure-entra";
			}
		}

		if (store) {
			const state = store.getState() as any;
			const userProfile = state.studiesSlice.userProfile;
			const userProfileId = state.studiesSlice.userProfileId;
			const role = state.studiesSlice.role;
			const supportedLanguages = state.studiesSlice.supportedLanguages;

			if (role) config.headers["role-id"] = role;
			if (supportedLanguages) config.headers["interface-language"] = supportedLanguages || [SupportedLanguageType.English];
			const isRestrictedRole = role === roleAccessType.Admin || role === roleAccessType.Support || role === roleAccessType.Developer;
			// if (userProfile && userProfileId && !isRestrictedRole) config.headers["profile-id"] = userProfileId;
			if (userProfile && userProfileId && role !== roleAccessType.Admin) config.headers["profile-id"] = userProfileId;
		}

		return config;
	},
	(error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		console.error("API Error Status:", error.response?.status);

		if (error.response?.status === 401 && !isEntraEnabled) {
			if (isCognitoEnabled) {
				// One-shot force refresh through Amplify, then replay the original
				// request. If refresh fails or the retry also 401s, drop the session
				// and bounce to /login (the in-app picker).
				if (originalRequest._retry) {
					await clearCognitoToken();
					redirectToInAppLogin();
					return Promise.reject(error);
				}

				originalRequest._retry = true;

				try {
					const newToken = await forceRefreshCognitoToken();
					if (!newToken) {
						await clearCognitoToken();
						redirectToInAppLogin();
						return Promise.reject(error);
					}
					originalRequest.headers.Authorization = `Bearer ${newToken}`;
					return axiosInstance(originalRequest);
				} catch (refreshError) {
					console.error("Cognito force refresh failed:", refreshError);
					await clearCognitoToken();
					redirectToInAppLogin();
					return Promise.reject(error);
				}
			}

			window.location.href = environment.REACT_APP_REDIRECT_URL || "";
			return Promise.reject(error);
		}

		if (error.response?.status === 401 && isEntraEnabled) {
			// If in Teams, try to refresh Teams token
			if (teamsAuthService.isRunningInTeams()) {
				if (originalRequest._retry) {
					console.error("Teams token refresh failed, user needs to re-authenticate");
					// In Teams, we can't redirect - show error message
					if (store) {
						store.dispatch(
							openForbiddenModal({
								message: "Your session has expired. Please close and reopen the app.",
								type: "SESSION_EXPIRED",
							})
						);
					}
					return Promise.reject(error);
				}

				originalRequest._retry = true;

				try {
					// Try to get a fresh Teams token
					const newToken = await teamsAuthService.getTeamsToken();

					if (newToken) {
						originalRequest.headers.Authorization = `Bearer ${newToken}`;
						return axiosInstance(originalRequest);
					}
				} catch (teamsError) {
					console.error("Failed to refresh Teams token:", teamsError);
					return Promise.reject(error);
				}
			}

			// Handle MSAL authentication (existing code)
			if (isAuthenticating) {
				return Promise.reject(error);
			}

			if (originalRequest._retry) {
				isAuthenticating = false;
				return Promise.reject(error);
			}

			originalRequest._retry = true;

			const inProgress = msalInstance.getActiveAccount() === null && sessionStorage.getItem("msal.interaction.status");

			if (inProgress) {
				return Promise.reject(error);
			}

			if (msalInstance) {
				try {
					await ensureMsalReady();

					const accounts = msalInstance.getAllAccounts();

					if (accounts.length === 0) {
						isAuthenticating = true;

						setTimeout(() => {
							msalInstance.loginRedirect(apiTokenRequest).catch((redirectError) => {
								console.error("Login redirect failed:", redirectError);
								isAuthenticating = false;
							});
						}, 0);

						return Promise.reject(error);
					}

					const response = await msalInstance.acquireTokenSilent({
						...apiTokenRequest,
						account: accounts[0],
						forceRefresh: true,
					});

					isAuthenticating = false;

					originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
					return axiosInstance(originalRequest);
				} catch (tokenError: any) {
					console.error("Token acquisition failed:", tokenError);

					if (tokenError.errorCode === "interaction_required" || tokenError.errorCode === "consent_required" || tokenError.errorCode === "login_required") {
						if (!isAuthenticating) {
							isAuthenticating = true;

							setTimeout(() => {
								msalInstance.loginRedirect(apiTokenRequest).catch((redirectError) => {
									console.error("Login redirect failed:", redirectError);
									isAuthenticating = false;
								});
							}, 0);
						}
					} else {
						isAuthenticating = false;
					}

					return Promise.reject(error);
				}
			} else {
				if (!isAuthenticating) {
					window.location.href = environment.REACT_APP_REDIRECT_URL || "";
				}
			}
		}

		if (error.response?.status === 403) {
			if (store) {
				store.dispatch(
					openForbiddenModal({
						message: error.response?.data?.detail?.message,
						type: error.response?.data?.detail?.error_type,
					})
				);
				const access_type = userAccessType.view;
				if (error.response?.data?.detail?.error_type === accessErrorTypes.COLLABORATOR_REMOVED_EDIT) {
					store.dispatch(updateSelectedStudy({ access_type: access_type }));
					store.dispatch(setAccessType({ access_type }));
				}
			}
		}

		if (error.response?.status === 429 && store) {
			const detail = error.response?.data?.detail as IGenai429Detail | undefined;
			if (detail && typeof detail === "object" && typeof detail.resets_at === "string") {
				store.dispatch(setBlockedFrom429(detail));
				// Avoid recursion: if the 429 came from the usage endpoint itself we already
				// hold the authoritative block payload — don't trigger another refetch.
				const failedUrl: string = (originalRequest?.url || error.config?.url || "") as string;
				const isGenaiUsageRequest = failedUrl.includes("/masters/genai-usage");
				if (!isGenaiUsageRequest) {
					try {
						window.dispatchEvent(new Event("genai-usage:refresh"));
					} catch (_) {
						// SSR / non-DOM contexts — safe to ignore.
					}
				}
			}
		}

		return Promise.reject(error);
	}
);

export default axiosInstance;
